<template>
  <div class="billing">
    <CurrVersions />
    <h1>Billing</h1>
    <div>
      <select v-model="customerId" @change="getBillingData">
        <option value="ffffffff-ffff-ffff-ffff-ffffffffffff" selected
          >&lt;All Customers&gt;</option
        >
        <option
          v-for="customer in customers"
          v-bind:key="customer.customerId"
          v-bind:value="customer.customerId"
          >{{ customer.customerName }}</option
        >
      </select>
      <datepicker
        :value="startDate"
        @selected="setStartDate"
        :minimum-view="'month'"
        :maximum-view="'month'"
        :disabled-dates="disabledDates"
      ></datepicker>
      -
      <datepicker
        :value="endDate"
        @selected="setEndDate"
        :minimum-view="'month'"
        :maximum-view="'month'"
        :disabled-dates="disabledDates"
      ></datepicker>
    </div>
    <div class="billing-totals">
      <table class="vgt-table">
        <tr>
          <td>Number of Months:</td>
          <td>{{ numberOfMonths }}</td>
        </tr>
        <tr>
          <td>Total Item Count:</td>
          <td>{{ totalItemCount }}</td>
        </tr>
      </table>

      <table class="vgt-table bordered">
        <thead>
          <tr>
            <th></th>
            <th>Total</th>
            <th>Monad</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td>Monthly Fee:</td>
            <td>{{ formatCurrency(totalMonthlyFee) }}</td>
            <td>{{ formatCurrency(monadMonthlyFee) }}</td>
          </tr>
          <tr>
            <td>Item Billing:</td>
            <td>{{ formatCurrency(totalItemBilling) }}</td>
            <td>{{ formatCurrency(monadItemBilling) }}</td>
          </tr>
          <tr>
            <td><b>Total:</b></td>
            <td>
              <b>{{ formatCurrency(totalBilling) }}</b>
            </td>
            <td>
              <b>{{ formatCurrency(monadTotalBilling) }}</b>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
    <div class="table-wrapper loader-container">
      <vue-good-table
        :columns="[
          {
            label: 'Customer Name',
            field: 'customerName'
          },
          {
            label: 'Billing Type',
            field: 'billingTypeName'
          },
          {
            label: 'Monthly Fee',
            field: 'billingMonthlyFee',
            type: decimal,
            formatFn: this.formatCurrency,
            sortFn: this.sortFn
          },
          {
            label: 'Item Count',
            field: 'itemCount',
            sortFn: this.sortFn
          },
          {
            label: 'Item Billing Total',
            field: 'itemBillingTotal',
            type: decimal,
            formatFn: this.formatCurrency,
            sortFn: this.sortFn
          },
          {
            label: 'Monad Billing Total',
            field: 'monadBillingTotal',
            type: decimal,
            formatFn: this.formatCurrency,
            sortFn: this.sortFn
          }
        ]"
        :rows="billingData"
        ><template slot="table-row" slot-scope="props">
          <span>
            {{ props.formattedRow[props.column.field] }}
          </span>
        </template></vue-good-table
      >
      <Loader :loading="loadingBilling" />
    </div>
  </div>
</template>

<script>
import service from "@/services/service.js";
import Loader from "@/components/Loader.vue";
import CurrVersions from "@/components/CurrVersions.vue";
import { VueGoodTable } from "vue-good-table";
import Datepicker from "vuejs-datepicker";

export default {
  components: { Loader, CurrVersions, VueGoodTable, Datepicker },
  computed: {
    numberOfMonths: function() {
      return (
        (this.endDate.getFullYear() - this.startDate.getFullYear()) * 12 +
        (this.endDate.getMonth() - this.startDate.getMonth() + 1)
      );
    },
    totalMonthlyFee: function() {
      let sum = 0;
      this.billingData.forEach(row => (sum += row.billingMonthlyFee));
      return sum * this.numberOfMonths;
    },
    totalItemCount: function() {
      let sum = 0;
      this.billingData.forEach(row => (sum += row.itemCount));
      return sum;
    },
    totalItemBilling: function() {
      let sum = 0;
      this.billingData.forEach(row => (sum += row.itemBillingTotal));
      return sum;
    },
    totalBilling: function() {
      return this.totalMonthlyFee + this.totalItemBilling;
    },
    monadMonthlyFee: function() {
      return this.totalMonthlyFee / 2;
    },
    monadItemBilling: function() {
      let sum = 0;
      this.billingData.forEach(row => (sum += row.monadBillingTotal));
      return sum;
    },
    monadTotalBilling: function() {
      return this.monadMonthlyFee + this.monadItemBilling;
    }
  },
  data() {
    return {
      billingData: [],
      customers: [],
      customerId: "ffffffff-ffff-ffff-ffff-ffffffffffff",
      startDate: new Date(new Date().getFullYear(), new Date().getMonth(), 1),
      endDate: new Date(new Date().getFullYear(), new Date().getMonth() + 1, 0),
      loadingBilling: false,
      currencySymbol: "£",
      disabledDates: {
        from: new Date()
      }
    };
  },
  created() {
    this.getBillingData();
    this.getCustomers();
    this.currencySymbol = service.getCurrencySymbol();
  },
  methods: {
    sortFn(x, y) {
      return x < y ? -1 : x > y ? 1 : 0;
    },
    setStartDate(date) {
      this.startDate = date;
      //let's always set the end date to end of this month
      this.endDate = new Date(
        this.startDate.getFullYear(),
        this.startDate.getMonth() + 1,
        0
      );
      this.getBillingData();
    },
    setEndDate(date) {
      this.endDate = new Date(date.getFullYear(), date.getMonth() + 1, 0);
      if (this.endDate < this.startDate) {
        this.startDate = new Date(
          this.endDate.getFullYear(),
          this.endDate.getMonth(),
          1
        );
      }
      this.getBillingData();
    },
    getBillingData() {
      var self = this;
      self.loadingBilling = true;
      service
        .getBillingData(
          this.customerId,
          this.formatDate(this.startDate),
          this.formatDate(this.endDate)
        )
        .then(response => {
          self.billingData = response.data;
          self.loadingBilling = false;
        })
        .catch(() => {
          self.loadingBilling = false;
        });
    },
    getCustomers() {
      var self = this;
      self.loadingCustomers = true;
      service
        .getCustomers()
        .then(response => {
          self.customers = response.data;
          self.loadingCustomers = false;
        })
        .catch(() => {
          self.loadingCustomers = false;
        });
    },
    formatCurrency: function(value) {
      return this.currencySymbol + value.toFixed(2);
    }
  }
};
</script>
